<script lang="ts">
	// The lock screen. What a till shows when it is paired and nobody is at it:
	// the shop's names first, then the picked cashier's passcode.
	//
	// It names the restaurant, the shop and the till beside the pad, and says
	// what the till was in the middle of: read once, when the screen appears,
	// because a cashier reading it is deciding whether to unlock, not
	// watching it move.

	import LockPad from '../panels/LockPad.svelte';
	import { message, type Language } from '../i18n/messages';
	import { PasscodeEntry } from '../till/passcode.svelte';
	import { untrack } from 'svelte';
	import { storedSummary, waitingTotal } from '../draft/draft.svelte';
	import type { components } from '../api/types';
	import { askWhileAtRest, lockAtRest, settledLockAsk } from '../till/rest';
	import { installAtRest, switchAtRest } from '../till/shell';

	type Identity = components['schemas']['TillIdentityBody'];
	type Session = components['schemas']['SessionBody'];

	interface Props {
		language: Language;
		till: Identity;
		onUnlocked: (session: Session) => void;
	}

	let { language, till, onUnlocked }: Props = $props();

	// The total the cart showed: its discount comes off the subtotal and the
	// delivery fee the cart kept, as the cart takes it (UC-35), with the
	// account's minor digits, which the stored cart does not hold.
	const waiting = untrack(() => {
		const stored = storedSummary(till.tillId);
		return stored && { ...stored, totalMinor: waitingTotal(stored, till.settings.minorDigits) };
	});

	const entry = new PasscodeEntry((session) => onUnlocked(session));
	$effect(() => () => entry.stop());

	// Nothing waits behind the lock and nobody is at it: the till is at
	// rest, so it asks the Bosla app's shell to switch to a newer screen
	// bundle the store kept, which reloads it to this same screen (#392).
	// Once it has been at rest longer than any print waits for its printer
	// (PRINTS_SETTLED_MS), it first asks to install a newer app update the
	// store kept (UC-57): with nobody signed in it cannot read the print
	// queue, and the wait lets every print on its way end. Once a cashier
	// comes to the lock it asks nothing more, even mid-ask.
	const atRest = $derived(
		lockAtRest({
			waiting: waiting !== null,
			// A picked name, a digit, a check, a refusal or a lockout's countdown
			// is a cashier at the lock, and a reload would take it away.
			typing: entry.picked !== null || entry.digits !== '' || entry.phase !== 'idle',
		}),
	);
	$effect(() => {
		if (atRest) return askWhileAtRest(settledLockAsk(installAtRest, switchAtRest));
	});
</script>

<section class="lock" data-testid="screen-lock">
	<LockPad
		{language}
		where="{till.account} · {till.shop} · {till.till}"
		whoTitle={message(language, 'lockWho')}
		title={message(language, 'lockEnterPasscode')}
		{entry}
		{waiting}
		minorDigits={till.settings.minorDigits}
		whereTestId="lock-where"
		dotsTestId="lock-dots"
		messageTestId="lock-error"
		padTestId="lock-pad"
		pickTestId="lock"
	/>
</section>

<style>
	.lock {
		--ctl-min: var(--tap-min);
		min-block-size: 100dvh;
		display: flex;
	}
</style>
